import { useCallback, useEffect, useState } from "react";

import {
  API_EMPLOYEE_CERTIFICATION_LIST,
  API_EMPLOYEE_CERTIFICATION_MASTER,
} from "../../../../config/Endpoints.js";
import { getApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";
import type {
  CertificationCategory,
  CertificationFilters,
  EmployeeCertificationRow,
} from "./certification-types.ts";

type PageResponse = {
  content?: EmployeeCertificationRow[];
  totalElements?: number;
};

function unwrap<T>(data: T | { data?: T }): T {
  if (data && typeof data === "object" && "data" in data) {
    return ((data as { data?: T }).data ?? data) as T;
  }
  return data as T;
}

export function useCertificationMaster() {
  const [categories, setCategories] = useState<CertificationCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getApi<CertificationCategory[]>(
      API_EMPLOYEE_CERTIFICATION_MASTER,
      accessToken(),
      "json",
      controller.signal,
    )
      .then(({ data }) => {
        const payload = unwrap(data);
        setCategories(Array.isArray(payload) ? payload : []);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setError("Failed to load certification options.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  return { categories, loading, error };
}

export function useCertificationList(
  filters: CertificationFilters,
  page: number,
  pageSize: number,
) {
  const [rows, setRows] = useState<EmployeeCertificationRow[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const refetch = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({
      page: String(Math.max(0, page - 1)),
      size: String(pageSize),
    });
    if (filters.employeeId) params.set("employeeId", filters.employeeId);
    if (filters.category) params.set("category", filters.category);
    if (filters.certificationId) {
      params.set("certificationId", filters.certificationId);
    }

    if (filters.certificateUploaded === "true" || filters.certificateUploaded === "false") {
      params.set("certificateUploaded", filters.certificateUploaded);
    }

    setLoading(true);
    setError(null);
    getApi<PageResponse>(
      `${API_EMPLOYEE_CERTIFICATION_LIST}?${params.toString()}`,
      accessToken(),
      "json",
      controller.signal,
    )
      .then(({ data }) => {
        const payload = unwrap(data);
        setRows(Array.isArray(payload.content) ? payload.content : []);
        setTotalElements(Number(payload.totalElements) || 0);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setRows([]);
          setTotalElements(0);
          setError("Failed to load employee certifications.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [filters, page, pageSize, revision]);

  return { rows, totalElements, loading, error, refetch };
}
